import { formatDate } from '@takumi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Paperclip, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, EmptyState } from '@/components/ui/primitives';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  note: string | null;
  createdAt: string;
  uploadedByName: string | null;
}

const MAX = 15 * 1024 * 1024;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.csv,.txt';
const size = (b: number) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Contracts, signed quotes, certificates… stored on the CRM server, downloaded as files (never rendered inline). */
export function AttachmentsCard({ orgId }: { orgId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const files = useQuery({ queryKey: ['attachments', orgId], queryFn: () => api.get<Attachment[]>(`/organizations/${orgId}/attachments`) });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/attachments/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attachments', orgId] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  async function upload(file: File) {
    if (file.size > MAX) return toast.error('הקובץ גדול מ-15MB');
    setUploading(true);
    try {
      const me = await api.get<{ csrfToken: string }>('/auth/me');
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`/api/v1/organizations/${orgId}/attachments`, { method: 'POST', body, credentials: 'same-origin', headers: { 'X-CSRF-Token': me.csrfToken } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new ApiError(res.status, data.message ?? 'ההעלאה נכשלה');
      toast.success('הקובץ הועלה');
      void qc.invalidateQueries({ queryKey: ['attachments', orgId] });
      void qc.invalidateQueries({ queryKey: ['timeline', orgId] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setUploading(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <Card>
      <CardHeader
        title="קבצים"
        icon={<Paperclip />}
        count={files.data?.length}
        actions={
          can('crm.write') && (
            <>
              <input ref={input} type="file" accept={ACCEPT} className="hidden" aria-label="בחירת קובץ" onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} />
              <Button size="sm" variant="ghost" loading={uploading} onClick={() => input.current?.click()}><Upload /> העלאה</Button>
            </>
          )
        }
      />
      {!files.data?.length ? (
        <EmptyState title="אין קבצים" description="חוזים, הצעות חתומות, תעודות כשרות…" />
      ) : (
        <ul className="divide-y divide-line/70 text-sm">
          {files.data.map((f) => (
            <li key={f.id} className="group flex items-center gap-2 px-4 py-2.5">
              <FileText className="size-4 shrink-0 text-muted" />
              <div className="min-w-0 flex-1">
                <a href={`/api/v1/attachments/${f.id}/download`} className="block truncate font-semibold hover:text-matcha-700 hover:underline">{f.fileName}</a>
                <div className="text-xs text-muted">{[size(f.sizeBytes), formatDate(f.createdAt), f.uploadedByName, f.note].filter(Boolean).join(' · ')}</div>
              </div>
              <a href={`/api/v1/attachments/${f.id}/download`} aria-label="הורדה" className="text-muted hover:text-ink"><Download className="size-4" /></a>
              {can('crm.write') && (
                <button aria-label="מחיקה" className="text-muted opacity-0 group-hover:opacity-100 hover:text-danger focus:opacity-100" onClick={() => confirm(`למחוק את ${f.fileName}?`) && remove.mutate(f.id)}>
                  <Trash2 className="size-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
